import { useCallback, useEffect, useState } from "react";
import { X, Loader2, Building2, Users, ShieldAlert, Play, Square } from "lucide-react";
import {
  getCurrentUser,
  getTeams,
} from "../../api/services/companyAdminService";
import {
  getStartedTeamScenarios,
  startTeamScenario,
  stopTeamScenario,
} from "../../api/services/labService";

export default function TeamChallengeModal({
  open,
  onClose,
  scenarioId,
  onChanged,
}) {
  const [orgName, setOrgName] = useState("");
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // Set of team ids that currently have an active run for THIS scenario.
  const [runningTeamIds, setRunningTeamIds] = useState(new Set());
  // team id currently being started/stopped (for per-row spinner).
  const [busyTeamId, setBusyTeamId] = useState(null);

  // Given the list of teams, fan out one /started?team_id= call per team,
  // and collect which teams have an active run for THIS scenario.
  const computeRunningTeams = useCallback(
    async (teamList) => {
      const now = Date.now();
      const results = await Promise.all(
        teamList.map(async (team) => {
          try {
            const data = await getStartedTeamScenarios({ teamId: team.id });
            const runs = data?.runs || [];
            const isRunning = runs.some(
              (run) =>
                run.scenario_id === scenarioId &&
                run.team_id === team.id &&
                new Date(run.expires_at).getTime() > now,
            );
            return isRunning ? team.id : null;
          } catch {
            return null;
          }
        }),
      );
      return new Set(results.filter(Boolean));
    },
    [scenarioId],
  );

  // Load org + teams + each team's running state whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    let isActive = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError("");
      setRunningTeamIds(new Set());
      try {
        const [me, teamsData] = await Promise.all([
          getCurrentUser(),
          getTeams({ page: 1, pageSize: 100 }),
        ]);
        if (!isActive) return;
        setOrgName(me?.organization_name || "");
        const items = Array.isArray(teamsData?.teams) ? teamsData.teams : [];
        setTeams(items);

        const running = await computeRunningTeams(items);
        if (isActive) setRunningTeamIds(running);
      } catch (error) {
        if (isActive) {
          setLoadError(error?.message || "Failed to load teams.");
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    load();
    return () => {
      isActive = false;
    };
  }, [open, computeRunningTeams]);

  const handleToggle = async (team) => {
    if (!scenarioId) return;
    const isRunning = runningTeamIds.has(team.id);
    setBusyTeamId(team.id);
    try {
      if (isRunning) {
        await stopTeamScenario({ scenarioId, teamId: team.id });
        setRunningTeamIds((prev) => {
          const next = new Set(prev);
          next.delete(team.id);
          return next;
        });
      } else {
        await startTeamScenario({ scenarioId, teamId: team.id });
        setRunningTeamIds((prev) => new Set(prev).add(team.id));
      }
      onChanged?.();
    } catch {
      // On error, re-derive this team's true state from the server.
      try {
        const data = await getStartedTeamScenarios({ teamId: team.id });
        const runs = data?.runs || [];
        const now = Date.now();
        const stillRunning = runs.some(
          (run) =>
            run.scenario_id === scenarioId &&
            run.team_id === team.id &&
            new Date(run.expires_at).getTime() > now,
        );
        setRunningTeamIds((prev) => {
          const next = new Set(prev);
          if (stillRunning) next.add(team.id);
          else next.delete(team.id);
          return next;
        });
      } catch {
        /* ignore */
      }
    } finally {
      setBusyTeamId(null);
    }
  };

  if (!open) return null;

  const runningCount = runningTeamIds.size;

  return (
    <div className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-(--color-bg)/70 rounded-sm shadow-2xl w-full max-w-2xl h-150 max-h-[calc(100vh-2rem)] border border-(--color-border) overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative flex items-start justify-between px-7 py-5 border-b border-(--color-border) shrink-0">
          {/* subtle top accent */}
          <div
            className="absolute top-0 left-0 right-0 h-px pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent, var(--color-primary), transparent)",
            }}
          />
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 rounded-md bg-(--color-primary)/10 border border-(--color-primary)/20 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-5 h-5 text-(--color-primary)" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-(--color-text-primary) leading-tight">
                Team Challenge
              </h2>
              <p className="text-xs text-(--color-text-muted) mt-1">
                Start or stop this challenge per team
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-sm text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col px-7 py-5 gap-5">
          {/* Organization (pinned) */}
          <div className="shrink-0">
            <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted) mb-2">
              Organization
            </p>
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-(--color-border) bg-(--color-bg)">
              <Building2 className="w-4 h-4 text-(--color-primary) shrink-0" />
              <span className="text-sm font-medium text-(--color-text-primary)">
                {isLoading ? "Loading..." : orgName || "—"}
              </span>
            </div>
          </div>

          {/* Teams (header pinned, list scrolls) */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex items-center justify-between mb-2 shrink-0">
              <p className="text-[10.5px] font-semibold uppercase tracking-widest text-(--color-text-muted)">
                Teams
              </p>
              {!isLoading && !loadError && teams.length > 0 && (
                <span className="text-[10.5px] font-medium text-(--color-text-muted)">
                  {runningCount} of {teams.length} running
                </span>
              )}
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pr-1 -mr-1">
              {isLoading ? (
                <div className="flex items-center justify-center h-full gap-2 text-(--color-text-muted)">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Loading teams...</span>
                </div>
              ) : loadError ? (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {loadError}
                </div>
              ) : teams.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-2">
                  <Users className="w-8 h-8 text-(--color-text-muted)/40" />
                  <p className="text-sm text-(--color-text-muted)">
                    No teams found for this organization.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {teams.map((team) => {
                    const isRunning = runningTeamIds.has(team.id);
                    const isBusy = busyTeamId === team.id;
                    return (
                      <div
                        key={team.id}
                        className={`flex items-center justify-between gap-3 px-4 py-3.5 rounded-md border transition-colors ${
                          isRunning
                            ? "border-(--color-primary)/30 bg-(--color-primary)/4"
                            : "border-(--color-border) hover:border-(--color-border)/80"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-md bg-(--color-bg) border border-(--color-border) flex items-center justify-center shrink-0">
                            <Users className="w-4 h-4 text-(--color-text-muted)" />
                          </div>
                          <div className="min-w-0">
                            <span className="block text-sm font-semibold text-(--color-text-primary) truncate">
                              {team.name}
                            </span>
                            {isRunning && (
                              <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-(--color-primary)">
                                <span className="w-1.5 h-1.5 rounded-full bg-(--color-primary) animate-pulse" />
                                Running
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleToggle(team)}
                          disabled={isBusy}
                          className={`shrink-0 min-w-23 px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                            isRunning
                              ? "bg-transparent border border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                              : "bg-(--color-primary)/10 border border-(--color-primary)/40 text-(--color-primary) hover:bg-(--color-primary)/20"
                          }`}
                        >
                          {isBusy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : isRunning ? (
                            <Square className="w-3.5 h-3.5" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          {isBusy
                            ? isRunning
                              ? "Stopping"
                              : "Starting"
                            : isRunning
                              ? "Stop"
                              : "Start"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-7 py-4 border-t border-(--color-border) flex justify-end shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 border border-(--color-border) text-(--color-text-primary) rounded-lg hover:bg-(--color-bg-overlay) transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}